type Touch = { platform: string | null; occurredAt: string };
type JourneySummary = { firstTouch: Touch | null; lastTouch: Touch | null; touches: Touch[] };

const LABELS: Record<string, string> = {
  meta: 'Facebook / Instagram ad',
  facebook: 'Facebook / Instagram ad',
  instagram: 'Facebook / Instagram ad',
  google: 'Google',
  website: 'Website',
  direct: 'Website',
  'landing page': 'Website',
  organic: 'Organic search',
  referral: 'Referral',
  phone: 'Phone call',
  email: 'Email',
  sms: 'Text message',
  whatsapp: 'WhatsApp',
  chat: 'Website chat',
  business_messaging: 'Messenger',
  import: 'Imported',
};

/** Plain-words label for a recorded source; unknown values are tidied, not hidden. */
export function leadSourceLabel(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return 'Not recorded';
  const known = LABELS[raw.toLowerCase()];
  if (known) return known;
  const words = raw.replace(/[_-]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Fixed month names: browsers disagree on "Sep" vs "Sept" for en-GB.
function day(iso: string, withYear: boolean): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    timeZone: 'Europe/London',
  }).formatToParts(new Date(iso));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const label = `${get('day')} ${MONTHS[get('month') - 1]}`;
  return withYear ? `${label} ${get('year')}` : label;
}

/**
 * The customer page's "Lead source": where they came from, and when. Tracking
 * detail (campaign IDs, website visits, Meta delivery) stays in the database.
 */
export function describeLeadSource(
  journey: JourneySummary | undefined,
  customerSource: string | null | undefined
): { source: string; detail: string | null } {
  const first = journey?.firstTouch ?? null;
  const source = leadSourceLabel(first?.platform || customerSource);
  if (!first) return { source, detail: null };

  const count = journey?.touches.length ?? 1;
  const last = journey?.lastTouch ?? first;
  if (count <= 1) return { source, detail: `Enquired ${day(first.occurredAt, true)}` };

  const sameYear =
    new Date(first.occurredAt).getUTCFullYear() === new Date(last.occurredAt).getUTCFullYear();
  return {
    source,
    detail: `First enquiry ${day(first.occurredAt, true)} · ${count} enquiries · latest ${day(last.occurredAt, !sameYear)}`,
  };
}
